import { useState } from "react";
import { HttpHelpCenterService } from "../../../Services/http.clients/http.helpcenter.client";
import { useTicketStore } from "../../../Stores/Ticket";
import { activateOnKey } from "../../../Helper/a11y";
import "./ArticleRating.scss";
import {
  ARTICLE_RATING_DEFAULTS,
  ArticleRatingStrings,
} from "./articleRatingDefaults";

type Rating = "bad" | "neutral" | "good";

// Resolve the language the article is actually shown in. The help center may
// only offer a subset of languages, so we mirror getHelpCenterLanguage(): use
// the visitor's language if it's configured, otherwise fall back to the help
// center's first configured language (not to English).
function resolveLanguage(helpcenterConfig: any, lang: string): string {
  const localization = helpcenterConfig?.localization;
  if (Array.isArray(localization) && localization.length > 0) {
    const short = lang?.substring(0, 2);
    const match =
      localization.find(
        (l: any) => l.language === lang || l.language?.startsWith(short),
      ) ?? localization[0];
    return match?.language ?? lang ?? "en";
  }
  return lang ?? "en";
}

function resolveStrings(
  helpcenterConfig: any,
  lang: string,
): ArticleRatingStrings {
  const effectiveLang = resolveLanguage(helpcenterConfig, lang);
  const short = effectiveLang?.substring(0, 2) ?? "en";

  const base =
    ARTICLE_RATING_DEFAULTS[effectiveLang] ??
    ARTICLE_RATING_DEFAULTS[short] ??
    ARTICLE_RATING_DEFAULTS.en;

  // Custom wording set in the dashboard (Help center -> Translations) lives in
  // helpcenterConfig.staticTranslations, keyed by language. It overrides the
  // built-in defaults above when present.
  const overrides = helpcenterConfig?.staticTranslations ?? {};
  const override = overrides[effectiveLang] ?? overrides[short] ?? {};

  return {
    articleRatingTitle: override.articleRatingTitle ?? base.articleRatingTitle,
    articleNoHelpRatingTitle:
      override.articleNoHelpRatingTitle ?? base.articleNoHelpRatingTitle,
    articleHelpButton: override.articleHelpButton ?? base.articleHelpButton,
  };
}

export default function ArticleRating({
  articleId,
  lang,
  helpcenterConfig,
}: {
  articleId: string;
  lang: string;
  helpcenterConfig: any;
}) {
  const [rating, setRating] = useState<Rating | null>(null);
  const [offerHelp, setOfferHelp] = useState(false);

  const strings = resolveStrings(helpcenterConfig, lang);

  const reportRating = (newRating: Rating) => {
    if (rating !== null) return;
    setRating(newRating);
    HttpHelpCenterService.getInstance().submitArticleFeedback(
      articleId,
      lang,
      newRating,
    );
    if (newRating === "bad" || newRating === "neutral") {
      setOfferHelp(true);
    }
  };

  const startChat = () => {
    useTicketStore.getState().createNewConversation({});
  };

  return (
    <div className="article-ratings">
      <div className="article-ratings-emojis-title">
        {strings.articleRatingTitle}
      </div>
      <div
        className={`article-ratings-emojis ${
          rating !== null ? "article-ratings-emojis--disabled" : ""
        }`}
      >
        <div
          className={`article-ratings-emojis-emoji ${
            rating === "bad" ? "article-ratings-emojis-emoji--selected" : ""
          }`}
          role="button"
          tabIndex={0}
          aria-label="Not helpful"
          aria-pressed={rating === "bad"}
          onClick={() => reportRating("bad")}
          onKeyDown={activateOnKey(() => reportRating("bad"))}
        >
          😞
        </div>
        <div
          className={`article-ratings-emojis-emoji ${
            rating === "neutral" ? "article-ratings-emojis-emoji--selected" : ""
          }`}
          role="button"
          tabIndex={0}
          aria-label="Somewhat helpful"
          aria-pressed={rating === "neutral"}
          onClick={() => reportRating("neutral")}
          onKeyDown={activateOnKey(() => reportRating("neutral"))}
        >
          😐
        </div>
        <div
          className={`article-ratings-emojis-emoji ${
            rating === "good" ? "article-ratings-emojis-emoji--selected" : ""
          }`}
          role="button"
          tabIndex={0}
          aria-label="Helpful"
          aria-pressed={rating === "good"}
          onClick={() => reportRating("good")}
          onKeyDown={activateOnKey(() => reportRating("good"))}
        >
          😁
        </div>
      </div>
      {offerHelp && (
        <div className="article-ratings-chat">
          <div className="article-ratings-emojis-title">
            {strings.articleNoHelpRatingTitle}
          </div>
          <div
            className="linktype-button"
            role="button"
            tabIndex={0}
            onClick={startChat}
            onKeyDown={activateOnKey(startChat)}
          >
            {strings.articleHelpButton}
          </div>
        </div>
      )}
    </div>
  );
}
